import Phaser from 'phaser';
import { TEXTURE_KEYS, DEPTHS } from '../systems/util/constants.js';

const FENCE_ON_MS = 1300;
const FENCE_OFF_MS = 800;
const FENCE_DAMAGE = 1;

/** Electric fence: a horizontal run of tiles that toggles damaging/safe on a duty cycle. */
export class ElectricFence extends Phaser.Physics.Arcade.StaticGroup {
  constructor(scene, x, y, tileSize, lengthTiles = 1) {
    super(scene.physics.world, scene);
    this.scene = scene;
    this.tileSize = tileSize;
    this.active_ = true;
    this._timer = FENCE_ON_MS;

    // Fence.png is a complete self-contained panel (both end posts + the
    // wire strands baked into one image), not a repeatable single-post
    // segment like the old art was — tiling a copy of it per tile would
    // draw a full post-wire-post panel in EVERY tile, i.e. duplicate posts
    // down the whole run instead of one continuous fence. So this places
    // one image, scaled to span the run's full width.
    // `x` is the first tile's center (see toWorld() in ChunkStitcher.js);
    // shift to the run's overall center. `y` is that row's center too, so
    // `y + tileSize/2` is the floor surface directly below it (same
    // "surface = row-center + 16" convention used in Player.js/Enemy.js).
    const totalW = tileSize * lengthTiles;
    const centerX = x + (totalW - tileSize) / 2;
    const surfaceY = y + tileSize / 2;

    const seg = this.create(centerX, surfaceY, TEXTURE_KEYS.HAZARD_FENCE);
    seg.setOrigin(0.5, 1);
    seg.setScale(totalW / seg.width);
    seg.setDepth(DEPTHS.HAZARDS);
    seg.setData('team', 'hazard');
    seg.setData('damage', FENCE_DAMAGE);
    seg.refreshBody();
  }

  update(time, delta) {
    this._timer -= delta;
    if (this._timer <= 0) {
      this.active_ = !this.active_;
      this._timer = this.active_ ? FENCE_ON_MS : FENCE_OFF_MS;
      this.children.iterate((seg) => {
        if (!seg) return;
        seg.setAlpha(this.active_ ? 1 : 0.25);
        seg.setData('damage', this.active_ ? FENCE_DAMAGE : 0);
      });
    }
  }

  /** For HitboxManager-style overlap callbacks. */
  isDamaging() {
    return this.active_;
  }
}

const SIGNAGE_TRIGGER_RANGE = 70;
const SIGNAGE_FALL_DAMAGE = 2;

/** Falling signage: dormant until the player passes beneath, then drops as a one-shot hazard. */
export class FallingSignage extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y, target) {
    super(scene, x, y, TEXTURE_KEYS.TILE_SOLID);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setTint(0xffe14d);
    this.setDepth(DEPTHS.HAZARDS);
    this.body.setAllowGravity(false);
    this.body.setImmovable(true);
    this.setData('team', 'hazard');
    this.setData('damage', 0);

    this.target = target;
    this.state = 'dormant'; // dormant -> falling -> settled
  }

  update() {
    if (this.state === 'dormant') {
      if (this.target && Math.abs(this.target.x - this.x) < SIGNAGE_TRIGGER_RANGE && this.target.y > this.y) {
        this.state = 'falling';
        this.body.setAllowGravity(true);
        this.body.setImmovable(false);
        this.setData('damage', SIGNAGE_FALL_DAMAGE);
      }
    } else if (this.state === 'falling' && this.body.blocked.down) {
      this.state = 'settled';
      this.body.setVelocity(0, 0);
      this.body.setImmovable(true);
      this.setData('damage', 0);
    }
  }
}
